import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ImageUploader } from "@/components/ImageUploader";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useParentCredits } from "@/hooks/useParentCredits";
import { toast } from "@/hooks/use-toast";
import { useNavigate, Link } from "react-router-dom";
import { AlertCircle, ShoppingBag, LogOut, User, LogIn } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { CookieBanner } from "@/components/CookieBanner";
import { z } from "zod";

const listingSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  description: z.string().trim().min(10, "Description must be at least 10 characters").max(1000, "Description must be less than 1000 characters"),
  category: z.string().min(1, "Category is required"),
  condition: z.string().min(1, "Condition is required"),
  price: z.string().optional(),
  contact_method: z.string().trim().min(1, "Contact method is required").max(200, "Contact method must be less than 200 characters"),
  images: z.array(z.string()).min(1, "At least one image is required").max(3, "Maximum 3 images allowed")
});

export default function CreateListing() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [ptaId, setPtaId] = useState<string | null>(null);
  const [ptaData, setPtaData] = useState<any>(null);
  const [parentId, setParentId] = useState<string | null>(null);
  const { balance, loading: balanceLoading } = useParentCredits(ptaId || undefined);
  const [shopSettings, setShopSettings] = useState<any>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    price: "",
    contact_method: "",
    images: [] as string[],
    condition: "",
    is_featured: false,
    is_clothing: false,
    garment_size: ""
  });

  const conditionOptions = [
    { value: "new", label: "New" },
    { value: "like_new", label: "Like New" },
    { value: "good", label: "Good" },
    { value: "fair", label: "Fair" }
  ];
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    // Wait for auth to finish loading before checking user
    if (authLoading) return;
    
    if (!user) {
      // Get PTA ID from URL if available
      const urlParams = new URLSearchParams(window.location.search);
      const ptaParam = urlParams.get('pta');
      if (ptaParam) {
        navigate(`/portal/${ptaParam}`);
      } else {
        navigate('/portal');
      }
      return;
    }
    fetchData();
  }, [user, authLoading, navigate]);

  // Redirect if marketplace is disabled
  useEffect(() => {
    if (ptaData && ptaData.show_marketplace_section === false) {
      toast({
        title: "Marketplace Unavailable",
        description: "The marketplace is not enabled for this PTA.",
        variant: "destructive"
      });
      navigate(`/fundraising/${ptaId}`);
    }
  }, [ptaData, navigate, ptaId]);

  const fetchData = async () => {
    if (!user) return;

    setLoading(true);

    // Get parent data
    const { data: parentData } = await supabase
      .from('parents')
      .select('id, pta_id')
      .eq('user_id', user.id)
      .single();

    if (!parentData) {
      // Check if user is a member - they need to register as parent via parent portal
      const { data: memberData } = await supabase
        .from('members')
        .select('pta_id')
        .eq('user_id', user.id)
        .single();

      if (memberData) {
        toast({
          title: "Parent registration required",
          description: "Marketplace listings require parent registration. Please register via the fundraising page.",
          variant: "destructive"
        });
        navigate(`/fundraising/${memberData.pta_id}`);
        return;
      }

      // User is not a parent - redirect to parent portal to register
      toast({
        title: "Registration required",
        description: "Please register as a parent to list items",
        variant: "destructive"
      });
      navigate('/portal');
      return;
    }

    setPtaId(parentData.pta_id);
    setParentId(parentData.id);

    // Get PTA data
    const { data: pta } = await supabase
      .from('ptas')
      .select('*')
      .eq('id', parentData.pta_id)
      .single();
    
    if (pta) {
      setPtaData(pta);
    }

    // Get shop settings
    const { data: settings } = await supabase
      .from('shop_settings')
      .select('*')
      .eq('pta_id', parentData.pta_id)
      .single();

    if (!settings || !settings.parent_selling_enabled) {
      toast({
        title: "Parent selling disabled",
        description: "Parent selling is not enabled for this PTA",
        variant: "destructive"
      });
      navigate(`/fundraising/${parentData.pta_id}`);
      return;
    }

    setShopSettings(settings);

    // Get categories
    const { data: categoriesData } = await supabase
      .from('shop_categories')
      .select('*')
      .eq('pta_id', parentData.pta_id)
      .order('display_order');

    setCategories(categoriesData || []);
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    if (!user || !parentId || !ptaId) {
      toast({
        title: "Error",
        description: "Please log in to create a listing",
        variant: "destructive"
      });
      return;
    }

    // Validate form data
    const validation = listingSchema.safeParse(formData);
    if (!validation.success) {
      const fieldErrors: Record<string, string> = {};
      validation.error.errors.forEach((error) => {
        if (error.path[0]) {
          fieldErrors[error.path[0].toString()] = error.message;
        }
      });
      setErrors(fieldErrors);
      toast({
        title: "Validation Error",
        description: "Please check the form for errors",
        variant: "destructive"
      });
      return;
    }

    if (!termsAccepted) {
      toast({
        title: "Terms Required",
        description: "Please accept the terms of use",
        variant: "destructive"
      });
      return;
    }

    const baseCost = shopSettings.credit_cost_per_post || 1;
    const featuredCreditCost = shopSettings.featured_listing_credit_cost ?? 2;
    const featuredCost = formData.is_featured ? featuredCreditCost : 0;
    const totalCost = baseCost + featuredCost;

    // Double-check credits (button should already be disabled, but this is a safety net)
    if ((balance?.credits_available || 0) < totalCost) {
      toast({
        title: "Insufficient Credits",
        description: `You need ${totalCost} credits to list this item${formData.is_featured ? ' (including 2 for featured)' : ''}. Please purchase more credits.`,
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);

    try {
      // Calculate expiry date if configured
      let expiryDate = null;
      if (shopSettings.post_expiry_days) {
        const expiry = new Date();
        expiry.setDate(expiry.getDate() + shopSettings.post_expiry_days);
        expiryDate = expiry.toISOString();
      }

      // Calculate total credit cost (base + featured)
      const baseCost = shopSettings.credit_cost_per_post;
      const featuredCreditCost = shopSettings.featured_listing_credit_cost ?? 2;
      const featuredCost = formData.is_featured ? featuredCreditCost : 0;
      const totalCost = baseCost + featuredCost;

      // Create the listing
      const { error: insertError } = await supabase
        .from('shop_items')
        .insert({
          name: formData.name.trim(),
          description: formData.description.trim(),
          category: formData.category,
          price: formData.price ? parseFloat(formData.price as string) : null,
          contact_method: formData.contact_method.trim(),
          images: formData.images,
          seller_parent_id: parentId,
          pta_id: ptaId,
          seller_type: 'parent',
          status: 'active',
          approval_status: shopSettings.parent_post_approval_required ? 'pending' : 'approved',
          expires_at: expiryDate,
          condition: formData.condition || null,
          is_featured: formData.is_featured,
          garment_size: formData.is_clothing && formData.garment_size ? formData.garment_size.trim() : null
        });

      if (insertError) throw insertError;

      // Deduct credits (base + featured)
      const { error: creditError } = await supabase
        .from('parent_credit_transactions')
        .insert({
          parent_id: parentId,
          pta_id: ptaId,
          amount: -totalCost,
          transaction_type: 'post_created',
          description: `Listing fee for: ${formData.name.trim()}${formData.is_featured ? ' (Featured)' : ''}`
        });

      if (creditError) throw creditError;

      // Update the parent_credits balance
      const { error: balanceError } = await supabase
        .from('parent_credits')
        .update({ 
          credits_available: (balance?.credits_available || 0) - totalCost,
          updated_at: new Date().toISOString()
        })
        .eq('parent_id', parentId)
        .eq('pta_id', ptaId);

      if (balanceError) throw balanceError;

      toast({
        title: "Success",
        description: shopSettings.parent_post_approval_required 
          ? "Your listing has been submitted for approval" 
          : "Your listing is now live!"
      });

      navigate(`/marketplace/my-listings?pta=${ptaId}`);
    } catch (error: any) {
      console.error('Error creating listing:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create listing",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate(`/fundraising/${ptaId}`);
  };

  const logoUrl = ptaData?.logo_url;

  if (!user) {
    return null;
  }

  if (authLoading || loading || balanceLoading) {
    return (
      <div className="min-h-screen bg-background">
        <header 
          className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border"
          style={{ backgroundColor: ptaData?.header_color ? `${ptaData.header_color}` : undefined }}
        >
          <div className="container flex h-20 items-center justify-between">
            <Link to={`/fundraising/${ptaId}`} className="flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt="PTA Logo" className="h-12 w-auto rounded object-cover" />
              ) : (
                <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                  <span className="text-lg">🏫</span>
                </div>
              )}
              <span className="font-semibold text-lg hidden sm:inline-block">
                {ptaData?.name || 'PTA Marketplace'}
              </span>
            </Link>
          </div>
        </header>
        <main className="container py-8">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header matching FundraisingPage */}
      <header 
        className="sticky top-0 z-50 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border"
        style={{ backgroundColor: ptaData?.header_color ? `${ptaData.header_color}` : undefined }}
      >
        <div className="container flex h-20 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to={`/fundraising/${ptaId}`} className="flex items-center gap-2">
              {logoUrl ? (
                <img src={logoUrl} alt="PTA Logo" className="h-12 w-auto rounded object-cover" />
              ) : (
                <div className="w-10 h-10 rounded bg-primary/10 flex items-center justify-center">
                  <span className="text-lg">🏫</span>
                </div>
              )}
              <span className="font-semibold text-lg hidden sm:inline-block">
                {ptaData?.name || 'PTA Marketplace'}
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-8">
              <Link to={`/fundraising/${ptaId}`} className="text-sm font-medium hover:text-primary transition-colors">
                Home
              </Link>
              <Link to={`/marketplace?pta=${ptaId}`} className="text-sm font-medium hover:text-primary transition-colors">
                Marketplace
              </Link>
            </nav>
          </div>

          <div className="flex items-center gap-3">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-9 w-9 rounded-full">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={user.user_metadata?.avatar_url} alt={user.email} />
                      <AvatarFallback className="bg-primary text-primary-foreground">
                        {user.email?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium text-sm">{user.email}</p>
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`/parent-profile`)}>
                    <User className="mr-2 h-4 w-4" />
                    My Account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`/marketplace?pta=${ptaId}`)}>
                    <ShoppingBag className="mr-2 h-4 w-4" />
                    Marketplace
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleSignOut}>
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button variant="ghost" size="sm" onClick={() => navigate('/auth')}>
                <LogIn className="mr-2 h-4 w-4" />
                Sign In
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="container py-8">
        <div className="max-w-2xl mx-auto space-y-6">
          <div>
            <h1 className="text-3xl font-bold mb-2">Create Listing</h1>
            <p className="text-muted-foreground">
              List an item for sale in the marketplace
            </p>
          </div>

          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Cost:</strong> {shopSettings?.credit_cost_per_post || 1} credit per listing {(shopSettings?.featured_listing_credit_cost ?? 2) > 0 && `(+ ${shopSettings?.featured_listing_credit_cost ?? 2} for featured)`} •{" "}
              <strong>Your balance:</strong> {balance?.credits_available || 0} credits
              {shopSettings?.post_expiry_days && (
                <> • <strong>Expires in:</strong> {shopSettings.post_expiry_days} days</>
              )}
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5" />
                Item Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Item Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., School Uniform - Age 7-8"
                    required
                    maxLength={100}
                    className={errors.name ? "border-destructive" : ""}
                  />
                  {errors.name && <p className="text-xs text-destructive">{errors.name}</p>}
                  <p className="text-xs text-muted-foreground">{formData.name.length}/100</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => setFormData({ ...formData, category: value })}
                    required
                  >
                    <SelectTrigger className={errors.category ? "border-destructive" : ""}>
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.name}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.category && <p className="text-xs text-destructive">{errors.category}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="price">Price (£) - Optional</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                  />
                  <p className="text-xs text-muted-foreground">Leave blank if free or negotiable</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe your item in detail..."
                    required
                    rows={5}
                    maxLength={1000}
                    className={errors.description ? "border-destructive" : ""}
                  />
                  {errors.description && <p className="text-xs text-destructive">{errors.description}</p>}
                  <p className="text-xs text-muted-foreground">{formData.description.length}/1000</p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contact">Contact Method *</Label>
                  <Input
                    id="contact"
                    value={formData.contact_method}
                    onChange={(e) => setFormData({ ...formData, contact_method: e.target.value })}
                    placeholder="e.g., Email me at parent@example.com or Text 07XXX XXXXXX"
                    required
                    maxLength={200}
                    className={errors.contact_method ? "border-destructive" : ""}
                  />
                  {errors.contact_method && <p className="text-xs text-destructive">{errors.contact_method}</p>}
                  <p className="text-xs text-muted-foreground">
                    This will be visible to buyers. Include your preferred contact method.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Condition *</Label>
                  <Select
                    value={formData.condition}
                    onValueChange={(value) => setFormData({ ...formData, condition: value })}
                    required
                  >
                    <SelectTrigger className={errors.condition ? "border-destructive" : ""}>
                      <SelectValue placeholder="Select condition" />
                    </SelectTrigger>
                    <SelectContent>
                      {conditionOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.condition && <p className="text-xs text-destructive">{errors.condition}</p>}
                </div>

                <div className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <Label htmlFor="clothing-toggle" className="text-sm font-medium cursor-pointer">Clothing Item</Label>
                    <p className="text-xs text-muted-foreground">Enable to add garment condition and size details</p>
                  </div>
                  <Switch
                    id="clothing-toggle"
                    checked={formData.is_clothing}
                    onCheckedChange={(checked) => setFormData({
                      ...formData,
                      is_clothing: checked,
                      ...(!checked ? { garment_size: "" } : {})
                    })}
                  />
                </div>

                {formData.is_clothing && (
                  <div className="space-y-4 rounded-lg border border-dashed p-4">
                    <div className="space-y-2">
                      <Label>Garment Condition</Label>
                      <Select
                        value={formData.condition}
                        onValueChange={(value) => setFormData({ ...formData, condition: value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select condition" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="used">Used</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="garment-size">Size</Label>
                      <Input
                        id="garment-size"
                        value={formData.garment_size}
                        onChange={(e) => setFormData({ ...formData, garment_size: e.target.value })}
                        placeholder="e.g., Age 7-8, M, 32in"
                        maxLength={50}
                      />
                      <p className="text-xs text-muted-foreground">Enter the size as shown on the garment label</p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Images * (Max 3, 10MB each)</Label>
                  <ImageUploader
                    images={formData.images}
                    onChange={(images) => setFormData({ ...formData, images })}
                    ptaId={ptaId || ''}
                    itemId="temp"
                  />
                  {errors.images && <p className="text-xs text-destructive">{errors.images}</p>}
                </div>

                <Card className={`border-2 ${formData.is_featured ? 'border-primary bg-primary/5' : 'border-dashed'}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        id="featured"
                        checked={formData.is_featured}
                        onCheckedChange={(checked) => {
                          const newFeatured = checked as boolean;
                          const baseCost = shopSettings?.credit_cost_per_post || 1;
                          const featuredCreditCost = shopSettings?.featured_listing_credit_cost ?? 2;
                          const newTotalCost = baseCost + (newFeatured ? featuredCreditCost : 0);
                          const availableCredits = balance?.credits_available || 0;
                          
                          if (newFeatured && availableCredits < newTotalCost) {
                            toast({
                              title: "Insufficient Credits",
                              description: `Featured listings require ${newTotalCost} credits. You have ${availableCredits} credits.`,
                              variant: "destructive"
                            });
                          }
                          setFormData({ ...formData, is_featured: newFeatured });
                        }}
                      />
                      <div className="space-y-1">
                        <Label htmlFor="featured" className="text-sm font-medium cursor-pointer">
                          Make this a Featured Listing (+{shopSettings?.featured_listing_credit_cost ?? 2} credits)
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Featured listings appear first at the top of the marketplace with a special banner.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-muted">
                  <CardHeader>
                    <CardTitle className="text-base">Terms of Use</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm text-muted-foreground whitespace-pre-wrap max-h-60 overflow-y-auto">
                      {shopSettings?.terms_of_use}
                    </div>
                    <div className="flex items-center gap-2 mt-4">
                      <Checkbox
                        id="terms"
                        checked={termsAccepted}
                        onCheckedChange={(checked) => setTermsAccepted(checked as boolean)}
                      />
                      <Label htmlFor="terms" className="text-sm font-normal">
                        I have read and agree to the terms of use
                      </Label>
                    </div>
                  </CardContent>
                </Card>

                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate(`/marketplace?pta=${ptaId}`)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  {(() => {
                    const baseCost = shopSettings?.credit_cost_per_post || 1;
                    const featuredCreditCost = shopSettings?.featured_listing_credit_cost ?? 2;
                    const featuredCost = formData.is_featured ? featuredCreditCost : 0;
                    const totalCost = baseCost + featuredCost;
                    const hasEnoughCredits = (balance?.credits_available || 0) >= totalCost;
                    
                    return (
                      <Button
                        type="submit"
                        disabled={submitting || !termsAccepted || !hasEnoughCredits}
                        className="flex-1"
                        title={!hasEnoughCredits ? `You need ${totalCost} credits (you have ${balance?.credits_available || 0})` : undefined}
                      >
                        {submitting ? "Submitting..." : `Create Listing (${totalCost} credit${totalCost !== 1 ? 's' : ''})`}
                      </Button>
                    );
                  })()}
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* Footer matching FundraisingPage */}
      <footer className="bg-muted/30 border-t border-border mt-16">
        <div className="container py-12">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <h4 className="font-semibold mb-3">Quick Links</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to={`/fundraising/${ptaId}`} className="text-muted-foreground hover:text-primary transition-colors">
                    Home
                  </Link>
                </li>
                <li>
                  <Link to={`/marketplace?pta=${ptaId}`} className="text-muted-foreground hover:text-primary transition-colors">
                    Marketplace
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3">Policies</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacy" className="text-muted-foreground hover:text-primary transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms" className="text-muted-foreground hover:text-primary transition-colors">
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3">Contact</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {ptaData?.shared_email && <li>{ptaData.shared_email}</li>}
                {ptaData?.charity_number && <li>Charity: {ptaData.charity_number}</li>}
              </ul>
            </div>
          </div>

          <div className="border-t border-border mt-8 pt-8 text-center text-sm text-foreground">
            <p>© {new Date().getFullYear()} The PTA Hub. All rights reserved.</p>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
}
